import PieChart from '@/components/PieChart';
import { ComputeUsageByActorLineChart } from './components/ComputeUsageByActorLineChart';
import TechnologyProgressionChart from '@/components/TechnologyProgressionChart';
import { motion, AnimatePresence, animate, useMotionValue, Variants } from 'motion/react';
import { useState, useEffect, useMemo, useRef } from 'react';
import NumberFlow from '@number-flow/react';
import Tooltip from '@/components/Tooltip';
import React from 'react';
import { PanelRow } from '@/panelData';
// Import our SVG icon components
import { OpenAILogo, WorldLogo } from '@/components/icons';
import AudioPlayer from './AudioPlayer';
import { useAudio } from './contexts/AudioContext';
// ------------------------------------------------------------------------
// The unified chart data contains an array of such datapoints.
export interface ChartData {
  data: PanelRow[];
}

const formatNumber = (num: number) => {
  if (num >= 1000) return `${(num / 1000).toFixed(0)}k`;
  return num.toString();
};


const percentFormatter = new Intl.NumberFormat('en-US', {
  style: 'percent',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
  maximumSignificantDigits: 2,
});

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0
});

const yearFormatter = (value: number) => `${value}`;


const computeFormatter = (value: number) => {
  // Format the number as x * 10^y FLOP/s
  const exponent = Math.floor(Math.log10(value));
  const mantissa = value / Math.pow(10, exponent);
  return <span className="font-mono">{mantissa.toFixed(1)} 10<span className="align-super text-[6.5px]">{exponent}</span> FLOP/month</span>;
};

export const thresholds = [
  // { label: "Coding copilot", value: 0.98 },
  { label: "Unreliable Agent", value: 1.05 },
  { label: "Reliable Agent", value: 1.4 },
//  { label: "Automated coder", value: 2.5 },
  { label: "Superhuman coder", value: 4 },
  { label: "Superhuman AI Researcher", value: 25 },
  { label: "Superhuman remote worker", value: 100 },

  { label: "Superintelligent AI Researcher", value: 250 },
  { label: "Generally Superintelligent", value: 1000 },
  { label: "Wildly Superintelligent", value: 15000 },
];

// Updated CapabilityHorizontalBarWithIconsAndLabels component with conditional fill color
const CapabilityHorizontalBarWithIconsAndLabels: React.FC<{
  capabilities: { name: string; value: number, longName: string }[];
}> = ({ capabilities }) => {
  const iconsByName: Record<string, string> = {
    Hacking: '/hacking.svg',
    Coding: '/coding.svg',
    Politics: '/political.svg',
    Bioweapons: '/bioweapons.svg',
    Robotics: '/robotics.svg',
    Forecasting: '/forecasting.svg',
  };

  // Set the maximum possible value for scaling the bar widths.
  const maxCapability = 4;

  // Helper to determine performance category
  const getCategory = (value: number): string => {
    if (value < 1) return "in the range of an amateur";
    else if (value < 2) return "in the range of a professional in this domain";
    else if (value < 3) return "better than the best human";
    else if (value < 4) return "wildly superhuman";
    return "wildly superhuman";
  };

  return (
    <div className="flex flex-wrap flex-col h-[100px] justify-around gap-1 pl-1 w-full pr-2">
      {capabilities.map((capability) => {
        if (!iconsByName[capability.name]) return false;
        // Calculate percent fill (capping at 100%)
        const percentage = Math.min(
          (capability.value / maxCapability) * 100,
          100
        );
        // Choose accent color if value > 1, otherwise fall back to black.
        const barColor = capability.value > 1 ? "var(--accent)" : "black";
        const tooltipBarColor = capability.value > 1 ? "var(--accent)" : "black";

        // For rendering the score: bold and accent if >1.
        const formattedValue =
          capability.value > 1 ? (
            <span className="font-bold">
              {capability.value.toFixed(1)}
            </span>
          ) : (
            <span>{capability.value.toFixed(1)}</span>
          );

        // Updated tooltip content for the mini chart using the same fill effect.
        const tooltipContent = (
          <div className="w-140 p-4 pb-2 text-xs">
            <p className="mb-2 text-xs mt-0">
              This bar measures the <strong>{capability.longName}</strong> ability of the current top performing AI.
            </p>
            <p className="mb-2 text-xs mt-0">
              A score of {formattedValue} indicates that the top performing AI is{" "}
              {getCategory(capability.value)}.
            </p>
            <div className="mb-2 text-xs mt-4">
              <div className="flex gap-[1px] rounded-sm overflow-hidden bg-[#cfcfcf] border border-white">
                {["Amateur", "Human Pro", "Superhuman", "Superhuman+"].map((label, index) => {
                  // Each segment represents a unit interval of the capability (maxCapability is 4)
                  // Compute the fill percentage for this segment:
                  const fillPercentage = Math.min(Math.max((capability.value - index) * 100, 0), 100);
                  return (
                    <div
                      key={label}
                      className="h-full w-1/4 flex items-center transition-all duration-500 ease-in-out z-1 relative"
                      style={{
                        background: `linear-gradient(to right, ${tooltipBarColor} var(--w), var(--vivid-background) var(--w))`,
                        "--w": `${fillPercentage}%`,
                        transition: `--w 0.5s`,
                      } as React.CSSProperties}
                    >
                      <span
                        className="text-transparent font-mono tracking-widest whitespace-nowrap [background:inherit] w-full h-full flex items-center pl-[4px] text-[8px]"
                        style={{
                          background: "inherit",
                          backgroundClip: "text",
                          filter: "invert(1) grayscale(1) contrast(1000)",
                        }}
                      >
                        {label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );

        return (
          <Tooltip key={capability.name} content={tooltipContent}>
            <div className="flex items-center w-1/2">
              {/* Bar Container */}
              <div className="w-full h-6 rounded-sm overflow-hidden relative">
                <div
                  className="h-full flex items-center transition-all duration-500 ease-in-out z-1 relative"
                  style={{
                    background: `linear-gradient(to right, ${barColor} calc(min(25%, var(--w))), white calc(min(25%, var(--w))), white calc(min(25%, var(--w)) + 1%), ${barColor} calc(min(25%, var(--w)) + 1%), ${barColor} var(--w), var(--vivid-background) var(--w))`,
                    "--w": `${percentage}%`,
                    transition: `--w 0.5s`,
                  } as React.CSSProperties}
                >
                  <span
                    className="text-transparent font-mono tracking-widest whitespace-nowrap [background:inherit] w-full h-full flex items-center pl-[8px] text-[10px]"
                    style={{
                      backgroundClip: "text",
                      filter: `invert(1) grayscale(1) contrast(1000)`,
                    }}
                  >
                    {iconsByName[capability.name] && (
                      <LogoWithPartialDynamicContrast
                        src={iconsByName[capability.name]}
                        alt={`${capability.name} icon`}
                        fillWidth={percentage > 25 ? 100 : ((percentage * 4) - 25) * 2}
                      />
                    )}
                    <span className="">{capability.name}</span>
                  </span>
                </div>
              </div>
            </div>
          </Tooltip>
        );
      })}
    </div>
  );
};

// New helper component for key statistics tooltips
const KeyStatTooltipContent: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="w-64 p-2 text-xs [&>p]:text-xs [&>p]:mt-0 [&>*:last-child]:mb-0">
    {children}
  </div>
);

interface HUDProps {
  currentDate: Date;
  defaultChartData: PanelRow[];
  chartData?: PanelRow[];
  skipEmergingTechnologies?: boolean;
}

// Define display modes for the HUD component
type DisplayMode = 'normal' | 'pip' | 'fullscreen';

const AnimatedMonth: React.FC<{ month: Date }> = ({ month }) => {
  const monthString = month.toLocaleDateString('en-US', { month: 'short' });
  return (
    <div className="relative h-[15px] w-7 text-baseline mt-[-1px]">
      <AnimatePresence mode="popLayout" initial={false}>
        <motion.span
          key={monthString}
          style={{ position: "absolute", left: 0 }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 1, y: -20 }}
          transition={{ duration: 0.3 }}
          className="font-system-mono mr-1"
        >
          {monthString}
        </motion.span>
      </AnimatePresence>
    </div>
  );
};

// A helper that returns the most recent entry (by date) from a sorted array
function getLatestEntry<T extends keyof PanelRow[1]>(data: PanelRow[], currentDate: Date, key: T): PanelRow[1][T] {
  // Filter any data that occurred at or before the current date
  const filtered = data.filter(([date, data]) => (date && new Date(date) <= (new Date(currentDate.getTime() + 24 * 60 * 60 * 1000))) && data[key] !== undefined); // +1 day to include the current date
  // If there is at least one, return the last item (most recent).
  // Otherwise, fall back to the first entry.
  return (filtered.length > 0 ? filtered.at(-1)! : data.at(-1)!)[1][key] as PanelRow[1][T];
}

const HUD: React.FC<HUDProps> = React.memo(function HUD({ currentDate, chartData, defaultChartData, skipEmergingTechnologies = false }) {
  // Use passed-in chart data or defaultChartData.
  const finalChartData = (chartData && chartData.length > 0) ? chartData : defaultChartData;
   
  // State to track display mode (normal, pip, fullscreen)
  // When in PIP mode, the HUD is displayed as a small floating window in the bottom-right corner of the screen
  // When in fullscreen mode, the HUD takes up the entire screen 
  // Normal mode is used for desktop screens where the HUD is displayed inline
  
  // Initial mode is 'normal' for SSR, then we'll update after mount
  const [displayMode, setDisplayMode] = useState<DisplayMode>('pip');
  const { isVisible: isAudioVisible } = useAudio();

  // Disable scrolling on the body when in fullscreen mode
  useEffect(() => {
    if (typeof document !== 'undefined') {
      if (displayMode === 'fullscreen') {
        // Disable scrolling on the body
        document.body.classList.add('overflow-hidden');
      } else {
        // Re-enable scrolling
        document.body.classList.remove('overflow-hidden');
      }
    }
    
    // Cleanup function to ensure scrolling is re-enabled when component unmounts
    return () => {
      if (typeof document !== 'undefined') {
        document.body.classList.remove('overflow-hidden');
      }
    };
  }, [displayMode]);

  // Existing use of getLatestEntry for different keys...
  const agentPopulationEntry = getLatestEntry(finalChartData, currentDate, "agentPopulation");
  const approvalEntry = getLatestEntry(finalChartData, currentDate, "approval");
  const revenueEntry = getLatestEntry(finalChartData, currentDate, "revenue");
  const valuationEntry = getLatestEntry(finalChartData, currentDate, "valuation");
  const importanceEntry = getLatestEntry(finalChartData, currentDate, "aiImportance");
  const datacenterEntry = getLatestEntry(finalChartData, currentDate, "datacenterSpending");
  const timelineEntry = getLatestEntry(finalChartData, currentDate, "agiTimelines");
  const capabilityEntry = getLatestEntry(finalChartData, currentDate, "capabilities");
  const computeEntry = getLatestEntry(finalChartData, currentDate, "compute");

  // NEW: Gather all compute usage datapoints from the unified chart data.
  const computeUsageDataPoints = useMemo(() => {
    const points: Array<{ date: string; OpenBrain: number; China: number; US2nd: number }> = [];
    finalChartData.forEach(([date, data]) => {
      const [OpenBrain, China, US2nd] = data.rdMultiple;
      points.push({ date, OpenBrain, China, US2nd });
    });
    // Optionally, sort the points by date for predictable ordering.
    points.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    return points;
  }, [finalChartData, currentDate]);

  // NEW: Compute the current OpenBrain speedup and then the current threshold label.
  const openMindSpeedup = computeUsageDataPoints.filter(d => new Date(d.date) <= currentDate).at(-1)?.OpenBrain || 0;

  const passedThresholds = thresholds.filter(t => openMindSpeedup >= t.value);
  const currentThresholdLabel = passedThresholds.length > 0 
    ? passedThresholds[passedThresholds.length - 1].label 
    : "AI Research Assistant";

  const currentCopies = agentPopulationEntry?.copies || 0;
  const greenBoxes = Math.min(Math.ceil(currentCopies / 1000), 900);
  
  // Toggle between PIP and fullscreen modes
  const togglePipFullscreen = () => {
    console.log("Toggling PIP/Fullscreen mode", displayMode);
    setDisplayMode(prevMode => prevMode === 'pip' ? 'fullscreen' : 'pip');
  };
  
  // Close fullscreen mode and return to PIP
  const closeFullscreen = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDisplayMode('pip');
  };
  
  // Animation variants for the motion component
  const variants = {
    pip: {
      position: 'fixed',
      top: `calc(100% - ${isAudioVisible ? '132.25px' : '95px'} - 0.75rem)`,
      left: '0.5rem',
      bottom: '0.75rem',
      right: '0.5rem',
      width: 'calc(100vw - 1rem)',
      height: 'min-content',
      minHeight: isAudioVisible ? '132.25px' : '95px',
      opacity: ((currentDate < new Date('2025-07-01')) && !isAudioVisible) ? 0 : 1,
      pointerEvents: ((currentDate < new Date('2025-07-01')) && !isAudioVisible) ? 'none' : 'auto',
      borderRadius: '8px',
      backgroundColor: 'var(--vivid-background)',
    },
    fullscreen: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: '100%',
      bottom: '100%',
      width: '100vw',
      height: '100vh',
      minHeight: '100vh',
      opacity: 1,
      borderRadius: '0px',
      backgroundColor: 'var(--vivid-background)',
      overflow: 'hidden',
    }
  };

  // Render a simplified version for PIP mode
  const renderPipContent = () => (
    <>
      {/* Date display - more compact for PIP mode */}
      <div className="flex font-system-mono text-xs absolute top-1">
        <div className="flex items-center justify-center w-[20.5px] h-[20.5px] mr-1 rounded-sm bg-black text-white">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 3V9H5V5H9V3H3Z" fill="currentColor"/>
            <path d="M3 21H9V19H5V15H3V21Z" fill="currentColor"/>
            <path d="M21 3H15V5H19V9H21V3Z" fill="currentColor"/>
            <path d="M15 21H21V15H19V19H15V21Z" fill="currentColor"/>
          </svg>
        </div>
        <div className="overflow-hidden px-2 rounded-sm bg-black opacity-100 z-[1] relative">
        <div className="whitespace-nowrap inline-flex text-white mix-blend-difference align-center gap-1 pt-[3px]">
            <AnimatedMonth month={currentDate} />
            <NumberFlow
              format={{ useGrouping: false }}
              value={currentDate.getFullYear()}
              className="font-system-mono text-right mt-[-1.5px]"
            />
          </div>
        </div>
      </div>
      
      {/* Only the top graph in a simplified format */}
      <div className="origin-top mt-[-7px] mb-[-16px]" style={{width: chartWidth - 8}}>
        <ComputeUsageByActorLineChart 
          key={`pip-chart`}
          currentDate={currentDate} 
          rdData={computeUsageDataPoints}
          width={chartWidth - 8}
          height={74}
          deactivateTooltip={true}
        />
      </div>
      <div onClick={e => e.stopPropagation()}>
        <AnimatePresence initial={false}>
          {isAudioVisible && <motion.div
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.5 }}
            >
            <AudioPlayer />
          </motion.div>}
        </AnimatePresence>
      </div>
    </>
  );

  // State to store the computed chart width
  const [chartWidth, setChartWidth] = useState(412);
  
  // Update chart width when window resizes or display mode changes
  useEffect(() => {
    console.log("Registering resize listener")
    const updateChartWidth = () => {
      console.log({displayMode, windowWidth: window.innerWidth})
      if (displayMode === 'fullscreen' && typeof window !== 'undefined') {
        setChartWidth(window.innerWidth - 16); // 32px for padding (16px on each side)
      } else if (displayMode === 'pip' && typeof window !== 'undefined') {
        setChartWidth(window.innerWidth - 16); // 32px for padding (16px on each side)
      } else {
        setChartWidth(430); // Default width for normal mode
      }
    };
    
    // Initial calculation
    updateChartWidth();
    
    // Add resize listener
    window.addEventListener('resize', updateChartWidth);
    
    // Cleanup
    return () => window.removeEventListener('resize', updateChartWidth);
  }, [displayMode]);

  
  // Render full content for normal and fullscreen modes
  const renderFullContent = (chartWidth: number) => (
    <>
      <Tooltip content={<div className="w-140 p-4 pb-2 text-xs">
        <p className="mb-2 text-xs mt-0">
          The current date for data displayed for these charts is <b>{currentDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</b>. 
        </p>
      </div>}>
      <div className={`flex mb-4 font-system-mono text-sm mt-1 ${displayMode === 'fullscreen' ? 'ml-14 mt-3' : ''}`}>
        <div className="overflow-hidden px-2 border border-foreground rounded-md bg-black opacity-100 z-[1] relative">
        <div className="whitespace-nowrap inline-flex text-white mix-blend-difference align-center gap-1 pt-[2px]">
            <AnimatedMonth month={currentDate} />
            <NumberFlow
              format={{ useGrouping: false }}
              value={currentDate.getFullYear()}
              className="font-system-mono text-right mt-[-1px]"
            />
          </div>
        </div>
      </div>
      </Tooltip>
      <div className={`grid ${displayMode === 'fullscreen' ? 'grid-small-mobile' : 'grid-small'} mt-[-37px]`}>
        <ComputeUsageByActorLineChart 
          key="full-chart-412-100"
          currentDate={currentDate} 
          rdData={computeUsageDataPoints} 
          width={chartWidth}
          height={100}
        />
        <PieChart
          data={Object.fromEntries(computeEntry.map(([key, value]) => ([
            key === "OpenBrain" ? "OpenBrain" : key === "DeepCent" ? "DeepCent" : key === "RestUs" ? "Rest of US" : "China", value
          ]))) as { OpenBrain: number, DeepCent: number, "Rest of US": number, China: number }}
          title="Compute"
          category="dateLabel"
          valueFormatter={computeFormatter}
          className="[grid-area:goalsPieChart] max-w-full"
        />
        {!skipEmergingTechnologies && <TechnologyProgressionChart
          currentDate={currentDate}
          className="[grid-area:techBuckets] mt-auto max-w-full"
        />}
        <div className={`[grid-area:keystats] ${displayMode === 'fullscreen' ? 'flex-col' : 'flex'} justify-between gap-2 items-center mb-5 mt-3 max-w-full overflow-x-hidden`}>
          <div className="flex items-center gap-2 rounded-lg py-1">
            <OpenAILogo size={28} className="h-7 w-7" />
            <div className="flex justify-between flex-grow gap-3">
              <Tooltip
                content={
                  <KeyStatTooltipContent>
                    <p className="mb-2 font-sans">
                      Net-Approval of OpenBrain. Ranges from -100 to 100.
                    </p>
                    <p>
                      <strong>Current Approval:</strong>{" "}
                      {percentFormatter.format(approvalEntry || 0)}
                    </p>
                  </KeyStatTooltipContent>
                }
              >
                <div className="flex flex-col">
                  <span className="text-[8.5px] font-mono">Approval</span>
                  <NumberFlow
                    value={approvalEntry || 0}
                    format={{
                      style: "percent",
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    }}
                    className="font-mono text-xs font-bold"
                  />
                </div>
              </Tooltip>
              <Tooltip
                content={
                  <KeyStatTooltipContent>
                    <p className="mb-2 font-sans">
                      OpenBrain's annualized revenue.
                    </p>
                    <p>
                      <strong>Current Revenue:</strong>{" "}
                      {currencyFormatter.format(revenueEntry || 0)}
                      /yr
                    </p>
                  </KeyStatTooltipContent>
                }
              >
                <div className="flex flex-col">
                  <span className="text-[8.5px] font-mono">Revenue</span>
                  <div className="flex items-center">
                    <NumberFlow
                      value={revenueEntry || 0}
                      format={{
                        style: "currency",
                        currency: "USD",
                        notation: "compact",
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      }}
                      className="font-mono text-xs font-bold"
                    />
                    <span className="font-mono text-xs font-bold">/yr</span>
                  </div>
                </div>
              </Tooltip>
              <Tooltip
                content={
                  <KeyStatTooltipContent>
                    <p className="mb-2 font-sans">
                      OpenBrain's estimated market value.
                    </p>
                    <p>
                      <strong>Current Valuation:</strong>{" "}
                      {currencyFormatter.format(valuationEntry || 0)}
                    </p>
                  </KeyStatTooltipContent>
                }
              >
                <div className="flex flex-col">
                  <span className="text-[8.5px] font-mono">Valuation</span>
                  <NumberFlow
                    value={valuationEntry || 0}
                    format={{
                      style: "currency",
                      currency: "USD",
                      notation: "compact",
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    }}
                    className="font-mono text-xs font-bold"
                  />
                </div>
              </Tooltip>
            </div>
          </div>

          {displayMode === 'fullscreen' ? <div className="border-b border-black w-full my-2" /> : <div className="border-l border-black h-1/2" />}

          <div className="flex items-center gap-2 rounded-lg py-1">
            <WorldLogo size={28} className="h-7 w-7" />
            <div className="flex justify-between flex-grow gap-3">
              <Tooltip
                content={
                  <KeyStatTooltipContent>
                    <p className="mb-2 font-sans">
                      What fraction of the U.S. population considers "AI" their answer to the question "What do you think is the most important problem facing the country today?".
                    </p>
                    <p>
                      <strong>Current Importance:</strong>{" "}
                      {percentFormatter.format(importanceEntry || 0)}
                    </p>
                  </KeyStatTooltipContent>
                }
              >
                <div className="flex flex-col">
                  <span className="text-[8.5px] font-mono">Importance</span>
                  <NumberFlow
                    value={importanceEntry || 0}
                    format={{
                      style: "percent",
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    }}
                    className="font-mono text-xs font-bold"
                  />
                </div>
              </Tooltip>
              <Tooltip
                content={
                  <KeyStatTooltipContent>
                    <p className="mb-2 font-sans">
                      Global annual datacenter spending.
                    </p>
                    <p>
                      <strong>Current Spending:</strong>{" "}
                      {currencyFormatter.format(datacenterEntry || 0)}
                      /yr
                    </p>
                  </KeyStatTooltipContent>
                }
              >
                <div className="flex flex-col">
                  <span className="text-[8.5px] font-mono">Datacenters</span>
                  <div className="flex items-center">
                    <NumberFlow
                      value={datacenterEntry || 0}
                      format={{
                        style: "currency",
                        currency: "USD",
                        notation: "compact",
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      }}
                      className="font-mono text-xs font-bold"
                    />
                    <span className="font-mono text-xs font-bold">/yr</span>
                  </div>
                </div>
              </Tooltip>
              <Tooltip
                content={
                  <KeyStatTooltipContent>
                    <p className="mb-2 font-sans">
                      Median timeline for AI experts until AI can accomplish every task that can be done on a computer better and more cheaply than human workers.
                    </p>
                    <p>
                      <strong>Projected Year:</strong>{" "}
                      {yearFormatter(timelineEntry || 0)}
                    </p>
                  </KeyStatTooltipContent>
                }
              >
                <div className="flex flex-col">
                  <span className="text-[8.5px] font-mono">Timeline</span>
                  <NumberFlow
                    value={timelineEntry || 0}
                    format={{ useGrouping: false }}
                    className="font-mono text-xs font-bold"
                  />
                </div>
              </Tooltip>
            </div>
          </div>
        </div>
        <Tooltip
          content={
            <div className="w-120 p-4 text-xs">
              <p className="mb-0 text-xs mt-0 font-sans">
                {currentThresholdLabel} copies actively engaged in research (most intelligent model equivalents) and their current thinking speed compared to humans (assumed to be 10 tokens/second).
              </p>
              <p className="mb-0 text-xs mt-2">
                There are currently <b>{formatNumber(currentCopies)}</b> copies thinking at <b>{(agentPopulationEntry.speed || 0) / 10} times</b> human speed.
              </p>
            </div>
          }
        >
          <motion.div className="[grid-area:agentPopulation] flex flex-col gap-1 rounded-lg px-1 py-3 max-w-full">
            <motion.div className="flex">
              <span className="text-[10.4px] tracking-tight font-mono">
                <span className="font-bold">
                  {currentCopies < 200000 ? <NumberFlow value={currentCopies} format={{ useGrouping: true }} /> : <NumberFlow value={currentCopies} format={{ useGrouping: true, notation: 'compact' }} />}
                </span> {currentThresholdLabel} copies thinking at{" "}
                <span className="font-bold">
                  <NumberFlow value={(agentPopulationEntry.speed || 0) / 10} format={{ useGrouping: false }} />x human speed
                </span>
              </span>
            </motion.div>
            <GreenBoxes targetBoxes={greenBoxes} />
          </motion.div>
        </Tooltip>
        <div className="[grid-area:capability] flex max-w-full overflow-hidden">
          <div className="[writing-mode:vertical-rl] scale-[-1] [font-variant:small-caps] text-[12px] mb-1 font-mono pt-[9px] pl-1">
            AI Capabilities
          </div>
          <CapabilityHorizontalBarWithIconsAndLabels
            capabilities={capabilityEntry.map(([name, value]) => ({ name, value, longName: name }))}
          />
        </div>
      </div>
    </>
  );

  return (<>
    <div className="hidden md:block requires-modern-css">
      {renderFullContent(430)}
    </div>
    <div
      className={getContainerClasses(displayMode)}
      style={(variants as any)[displayMode]}
      onClick={displayMode === 'pip' ? togglePipFullscreen : undefined}
    >
      {/* Close button for fullscreen mode */}
      {displayMode === 'fullscreen' && (
        <motion.button
          onClick={closeFullscreen}
          className="absolute top-4 left-4 z-50 bg-gray-200 hover:bg-gray-300 rounded-full p-2"
          aria-label="Close fullscreen view"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          transition={{ duration: 0.2 }}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 4L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </motion.button>
      )}
      
      {/* Render content based on display mode */}
      <motion.div
        key={displayMode}
        initial={{ opacity: 1 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.2 }}
      >
        {displayMode === 'pip' ? renderPipContent() : renderFullContent(chartWidth)}
      </motion.div>
    </div>
  </>
    
  );
});

export default HUD;

// ------------------------------------------------------------------------
// GreenBoxes component (unchanged)
const GreenBoxes: React.FC<{ targetBoxes: number }> = ({ targetBoxes }) => {
  const [animatedBoxes, setAnimatedBoxes] = useState(targetBoxes);
  const countMotion = useMotionValue(targetBoxes);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(400);
  
  // Box and gap dimensions in pixels
  const boxWidth = 8;
  const boxGap = 2;
  
  // Calculate boxes per row based on container width
  const boxesPerRow = Math.max(1, Math.floor((containerWidth + boxGap) / (boxWidth + boxGap)) - 1);
  
  useEffect(() => {
    const controls = animate(countMotion, targetBoxes, {
      type: 'tween',
      duration: 0.5,
      onUpdate: (latest) => {
        setAnimatedBoxes(Math.round(latest));
      },
    });
    return controls.stop;
  }, [targetBoxes, countMotion]);
  
  // Measure container width after mount and on resize
  useEffect(() => {
    if (!containerRef.current) return;
    
    // Initial measurement
    setContainerWidth(containerRef.current.clientWidth);
    
    // Create ResizeObserver to detect container size changes
    const resizeObserver = new ResizeObserver(entries => {
      if (entries[0]) {
        setContainerWidth(entries[0].contentRect.width);
      }
    });
    
    resizeObserver.observe(containerRef.current);
    
    // Cleanup
    return () => {
      if (containerRef.current) {
        resizeObserver.unobserve(containerRef.current);
      }
      resizeObserver.disconnect();
    };
  }, []);
  
  const numberOfRows = Math.ceil(animatedBoxes / boxesPerRow);
  const targetNumberOfRows = Math.ceil(targetBoxes / boxesPerRow);
  const boxHeight = 8;
  const gapHeight = 4;
  const targetHeight =
    targetNumberOfRows * boxHeight + (targetNumberOfRows - 1) * gapHeight;
  
  const hasMoreThanMaxCopies = animatedBoxes === 900;

  return (
    <motion.div
      ref={containerRef}
      animate={{ height: `${targetHeight}px` }}
      initial={{ height: `${targetHeight}px` }}
      transition={{ duration: 0.35, ease: 'easeInOut' }}
      style={{
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        gap: `${gapHeight}px`,
        width: '100%', // Ensure the container takes full width
        maxHeight: 'calc(90vh - 400px)'
      }}
    >
      {containerWidth > 0 && Array.from({ length: numberOfRows }).map((_, rowIndex) => {
        const startIndex = rowIndex * boxesPerRow;
        const endIndex = Math.min(animatedBoxes, startIndex + boxesPerRow);
        const rowBoxCount = endIndex - startIndex;
        return (
          <div key={`row-${rowIndex}`} className="flex gap-[2px]">
            {Array.from({ length: rowBoxCount }).map((_, boxIndex) => (
              <div
                key={`green-${rowIndex}-${boxIndex}`}
                className="w-[8px] h-[8px] bg-[var(--accent)] rounded-[1px]"
              />
            ))}
            {rowIndex === numberOfRows - 1 && hasMoreThanMaxCopies && <div className="w-[16px] h-[8px] flex items-center">...</div>}
          </div>
        );
      })}
    </motion.div>
  );
};

// LogoWithPartialDynamicContrast component
const LogoWithPartialDynamicContrast: React.FC<{
  src: string;
  alt: string;
  // Assuming --w (the fill percentage) is also available on the parent element,
  // you could pass it as a prop (if computed in JS) or use a CSS variable.
  fillWidth: number;
}> = ({ src, alt, fillWidth }) => {
  return (
    <div className="relative inline-block w-4 h-4 mr-4">
      {fillWidth >= 100 ? 
        <img src={src} alt={alt} className="max-w-none w-4 h-4 object-contain" /> 
      : <>
        <img src={src} alt={alt} className="max-w-none w-4 h-4 object-contain" style={{ filter: 'brightness(0) invert(1)' }} />
        <img
          src={src}
          alt={alt}
          className="max-w-none w-4 h-4 object-contain absolute top-0 left-0 pointer-events-none z-1"
          style={{
            // The mask is defined to only reveal the portion up to fillWidth%.
            maskImage: `linear-gradient(to right, white ${Math.max(fillWidth, 0)}%, transparent ${Math.max(fillWidth, 0)}%)`,
            // Alternatively, you might use clip-path if you prefer.
            filter: 'brightness(0) invert(1)',
            mixBlendMode: 'difference',
          }}
        />
      </>}
    </div>
  );
};

function getContainerClasses(displayMode: DisplayMode) {
  const baseClasses = `z-50 block md:hidden requires-modern-css transition-[height, opacity, width, top, left, right, bottom, min-height] duration-700`;

  switch (displayMode) {
    case 'pip':
      return `${baseClasses} 
      shadow-lg rounded-lg cursor-pointer origin-bottom-right 
      hud-pip overflow-hidden p-[5px] 
      bg-[var(--vivid-background)]`; // Wide and short for PIP
    case 'fullscreen':
      return `${baseClasses} p-2 overflow-auto hud-fullscreen bg-[var(--vivid-background)]`;
  }
}
